import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Card, Container, Grid, Link, Typography } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { Footer, MarketingFinalCta, SectionHeading } from '../components/homepage';
import { DEMO_REQUEST_MAILTO, FOUNDER_MAILTO, FOUNDER_EMAIL, SALES_EMAIL } from '../config/contact';
import {
  marketingCardSx,
  marketingDisplayFont,
  marketingHeroGridSx,
  marketingMutedSectionSx,
  marketingPalette,
  marketingPrimaryButtonSx,
  marketingSecondaryButtonSx,
  marketingSectionSx,
} from '../components/homepage/marketingSystem';

const founderNote = {
  kicker: 'Founder note · Mike Lee',
  // Hero card shows only the first two paragraphs — keep it tight
  heroParas: [
    'I spent most of my career inside payments teams — at Stripe, BILL, eBay — watching smart operators make the same move over and over: rip out one layer and replace it, hoping the next one will finally make conversion, cost, and risk stop pulling against each other. It never did.',
    'The problem is never that the stack is missing a connection. The problem is that no layer in the stack scores the tradeoff in real time, against the priorities the business actually has that quarter. PSPs execute. Orchestrators route. Fraud tools flag. None of them decide — in the moment, with the weights the business cares about right now — which move is actually best. That decision has to live a layer above everything else.',
  ],
  // Third paragraph shown in its own section below
  resolution: "Ormal is that layer. We built it to work on top of what a team already runs — because the teams we want to help don't need another migration. They need better decisions on the stack they have.",
  closer: "If that sounds like your stack too, I'd want to hear about it. — Mike",
};

const capabilities = [
  {
    label: 'Payments depth',
    companies: ['Stripe', 'BILL', 'eBay'],
    body: 'Real operating experience across approval, routing, economics, and risk — built and run, not advisory-only.',
    detail: 'We have managed the same problems our clients face: volume commitments, interchange optimization, chargeback defense, and cross-border approval gaps.',
  },
  {
    label: 'Applied AI & ML',
    companies: ['Amazon'],
    body: 'Model-building experience brought into payments optimization. Strong models grounded by real payments judgment.',
    detail: 'Production ML engineering applied to a domain where model quality compounds — scoring 12–40 payment paths per transaction in under 150ms is not a research prototype.',
  },
  {
    label: 'Domain advisors',
    companies: ['Senior payments operators', 'Senior AI practitioners'],
    body: "Advisors who have sat on both sides of the problems we're solving — operators and practitioners, not just analysts.",
    detail: 'Operating-level experience, not advisory-only backgrounds. They help shape what the product decides, not just how the pitch lands.',
  },
  {
    label: 'Specialist delivery',
    companies: ['On-demand depth'],
    body: 'Focused engineering support engaged where the work needs execution depth. Depth where the problem demands it.',
    detail: 'We engage specialist engineering for client-specific integration work rather than scaling a generalist delivery team. The depth goes where the problem is.',
  },
];

const engagementSteps = [
  {
    step: 'Step 1',
    pricing: 'Free',
    title: 'Payment Health Check',
    body: 'Share as little as 30 days of transaction data — anonymized if needed. One week later you have a quantified opportunity report showing expected lift across conversion, cost, and risk.',
    detail: "Or we tell you honestly there isn't one.",
    tag: '1 week · No commitment',
    accent: true,
  },
  {
    step: 'Step 2',
    pricing: 'Paid pilot',
    title: 'Proof of Concept',
    body: '30–90 day live pilot measured against your own baseline. Weekly readout with attributed lift before any annual commitment.',
    detail: 'You see the lift before you sign anything beyond the pilot.',
    tag: '30–90 days',
    accent: false,
  },
  {
    step: 'Step 3',
    pricing: 'Annual',
    title: 'Expand',
    body: 'More flows, more methods, more geographies. The model learns your stack over time — compounding lift as we incorporate more of your business context.',
    detail: 'Tier pressure, chargeback exposure, market-level risk — all factored in over time.',
    tag: 'Annual · Compounding lift',
    accent: false,
  },
];

const AboutUs: React.FC = () => {
  return (
    <Box className="marketing-layout" sx={{ width: '100%', overflowX: 'hidden' }}>
      <Helmet>
        <link rel="canonical" href="https://ormal.io/about" />
      </Helmet>

      {/* MISSION HERO — founder note prominent in the hero card */}
      <Box component="section" sx={{ ...marketingSectionSx, pt: { xs: 5.5, md: 7.5 }, pb: { xs: 6, md: 7 }, overflow: 'hidden' }}>
        <Box sx={marketingHeroGridSx} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 3.5, lg: 5 }} alignItems="center">
            <Grid item xs={12} lg={5}>
              <SectionHeading
                eyebrow="About"
                title="Payments expertise, AI experience, and early proof."
                description="Ormal exists to help companies of any size use payments more strategically — the kind of payment intelligence and decision quality that previously required larger enterprise payment organizations."
                marginBottom={2.5}
              />
              <Box sx={{ mb: 2.2 }}>
                <Typography sx={{ color: marketingPalette.body, lineHeight: 1.7, maxWidth: 480 }}>
                  AI-first, but not AI-only. Strong models grounded by real payments, risk, and implementation judgment — from people who have built and run payment stacks, not just advised on them.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.4 }}>
                <Button component="a" href={DEMO_REQUEST_MAILTO} endIcon={<ArrowForward />} sx={marketingPrimaryButtonSx}>
                  Request Demo
                </Button>
                <Button component={RouterLink} to="/resources" sx={marketingSecondaryButtonSx}>
                  Explore insights
                </Button>
              </Box>

              {/* Traction strip */}
              <Box
                sx={{
                  mt: 3.2,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1.4,
                  width: '100%',
                  maxWidth: 480,
                }}
              >
                {[
                  { value: '25', label: 'Health Checks', detail: 'Completed through Q1 2026' },
                  { value: '2', label: 'Published pilots', detail: 'Real numbers, attributed results' },
                  { value: '200K+', label: 'Transactions', detail: 'Analyzed to date' },
                ].map((s) => (
                  <Box
                    key={s.label}
                    sx={{
                      pl: 1.3,
                      borderLeft: '2px solid rgba(15,111,255,0.35)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: marketingDisplayFont,
                        fontSize: { xs: '1.05rem', md: '1.18rem' },
                        fontWeight: 400,
                        color: marketingPalette.accent,
                        letterSpacing: '-0.02em',
                        lineHeight: 1.1,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {s.value}
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 400, color: marketingPalette.headline, lineHeight: 1.25 }}>
                      {s.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.64rem', fontWeight: 400, color: marketingPalette.muted, lineHeight: 1.3 }}>
                      {s.detail}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} lg={7}>
              <Card
                sx={{
                  ...marketingCardSx,
                  p: { xs: 2.6, md: 3.2 },
                  borderLeft: `4px solid ${marketingPalette.accent}`,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Typography sx={{ color: marketingPalette.accent, fontSize: '0.68rem', fontWeight: 400, letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.8 }}>
                  {founderNote.kicker}
                </Typography>
                <Box sx={{ display: 'grid', gap: 1.4, maxWidth: 720 }}>
                  {founderNote.heroParas.map((paragraph, index) => (
                    <Typography
                      key={`founder-${index}`}
                      sx={{
                        color: marketingPalette.body,
                        fontSize: index === 0 ? { xs: '0.98rem', md: '1.04rem' } : '0.95rem',
                        lineHeight: 1.75,
                      }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </Box>
                <Box
                  sx={{
                    mt: 1.6,
                    pt: 1.4,
                    borderTop: '1px dashed rgba(196,212,232,0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.7,
                  }}
                >
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: marketingPalette.accent, flexShrink: 0 }} />
                  <Typography sx={{ color: marketingPalette.muted, fontSize: '0.72rem', fontWeight: 400, letterSpacing: '0.06em' }}>
                    Continued below
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CAPABILITIES — experience as what we bring, not who we are */}
      <Box component="section" sx={marketingMutedSectionSx}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="What we bring"
            title="Experience framed as capability, not as headcount."
            description="Built by people who have worked inside payment teams at Stripe, BILL, eBay, and Amazon. Our advisors come from payments and AI."
            marginBottom={2.8}
          />
          <Grid container spacing={2.4}>
            {capabilities.map((cap) => (
              <Grid item xs={12} sm={6} md={6} key={cap.label}>
                <Card sx={{ ...marketingCardSx, height: '100%', p: { xs: 2.4, md: 2.8 } }}>
                  <Typography sx={{ color: marketingPalette.accent, fontSize: '0.72rem', fontWeight: 400, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.7 }}>
                    {cap.label}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.45, mb: 1 }}>
                    {cap.companies.map((company) => (
                      <Box
                        key={company}
                        sx={{
                          px: 0.7,
                          py: 0.25,
                          borderRadius: 0.5,
                          border: '1px solid rgba(196,212,232,0.9)',
                          backgroundColor: '#f8fbff',
                        }}
                      >
                        <Typography sx={{ color: marketingPalette.headline, fontSize: '0.66rem', fontWeight: 400 }}>
                          {company}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Typography sx={{ color: marketingPalette.body, lineHeight: 1.65, fontSize: '0.88rem', mb: 0.8 }}>
                    {cap.body}
                  </Typography>
                  <Typography sx={{ color: marketingPalette.muted, lineHeight: 1.62, fontSize: '0.82rem' }}>
                    {cap.detail}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FOUNDER NOTE RESOLUTION */}
      <Box component="section" sx={{ ...marketingSectionSx, pt: { xs: 3, md: 4 }, pb: { xs: 4, md: 5 } }}>
        <Container maxWidth="md">
          <Box
            sx={{
              px: { xs: 2.6, md: 3.6 },
              py: { xs: 2.4, md: 3.2 },
              borderRadius: 1.25,
              borderLeft: `4px solid ${marketingPalette.accent}`,
              border: '1px solid rgba(196,212,232,0.9)',
              borderLeftWidth: '4px',
              backgroundColor: '#fafcff',
            }}
          >
            <Typography sx={{ color: marketingPalette.body, fontSize: { xs: '1rem', md: '1.08rem' }, lineHeight: 1.78, mb: 1.8 }}>
              {founderNote.resolution}
            </Typography>
            <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontStyle: 'italic', lineHeight: 1.55 }}>
              {founderNote.closer}
            </Typography>
            <Typography sx={{ mt: 1.6, fontSize: '0.92rem', lineHeight: 1.6 }}>
              <Link href={FOUNDER_MAILTO} sx={{ color: marketingPalette.accentDark, textDecoration: 'none', fontWeight: 500 }}>
                {FOUNDER_EMAIL}
              </Link>
              <Box component="span" sx={{ color: marketingPalette.muted }}>
                {' '}· goes straight to Mike. For anything commercial,{' '}
              </Box>
              <Link href={DEMO_REQUEST_MAILTO} sx={{ color: marketingPalette.accentDark, textDecoration: 'none', fontWeight: 500 }}>
                {SALES_EMAIL}
              </Link>
              <Box component="span" sx={{ color: marketingPalette.muted }}>.</Box>
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ENGAGEMENT MODEL — 3 steps from deck slide 10 */}
      <Box component="section" sx={marketingSectionSx}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="How we start"
            title="Start with a Health Check."
            description="Share as little as 30 days of transaction data — anonymized if needed. One week later you have a quantified opportunity report, or we tell you honestly there isn't one."
            marginBottom={3}
          />
          <Grid container spacing={2.4}>
            {engagementSteps.map((step, index) => (
              <Grid item xs={12} md={4} key={step.step}>
                <Card
                  sx={{
                    ...marketingCardSx,
                    height: '100%',
                    p: { xs: 2.2, md: 2.6 },
                    borderTop: step.accent
                      ? `3px solid ${marketingPalette.accent}`
                      : '3px solid rgba(196,212,232,0.9)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
                    <Typography sx={{ color: marketingPalette.accent, fontFamily: 'monospace', fontSize: '0.64rem', fontWeight: 400, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      {step.step}
                    </Typography>
                    <Box
                      sx={{
                        px: 0.8,
                        py: 0.25,
                        borderRadius: 0.5,
                        border: '1px solid',
                        borderColor: step.accent ? 'rgba(15,111,255,0.28)' : 'rgba(196,212,232,0.9)',
                        backgroundColor: step.accent ? 'rgba(15,111,255,0.08)' : '#f8fbff',
                      }}
                    >
                      <Typography sx={{ color: step.accent ? marketingPalette.accent : marketingPalette.muted, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        {step.pricing}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: { xs: '1.08rem', md: '1.2rem' }, lineHeight: 1.28, mb: 0.75 }}>
                    {step.title}
                  </Typography>
                  <Typography sx={{ color: marketingPalette.body, lineHeight: 1.7, fontSize: '0.88rem', mb: 0.75 }}>
                    {step.body}
                  </Typography>
                  <Typography sx={{ color: marketingPalette.muted, lineHeight: 1.6, fontSize: '0.82rem', fontStyle: 'italic', mb: 1.4 }}>
                    {step.detail}
                  </Typography>
                  <Box sx={{ pt: 1, borderTop: '1px dashed rgba(196,212,232,0.9)', display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Box
                      sx={{
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        backgroundColor: step.accent ? marketingPalette.accent : 'rgba(148,163,184,0.5)',
                        flexShrink: 0,
                      }}
                    />
                    <Typography sx={{ color: marketingPalette.muted, fontSize: '0.68rem', fontWeight: 400, letterSpacing: '0.06em' }}>
                      {step.tag}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <MarketingFinalCta
        kicker="Get started"
        heading="Find out what's in your payment data."
        body="Share as little as 30 days of transaction history — 90 days gives us more signal. One week later you have a quantified opportunity report — or we tell you honestly there isn't one."
        secondary={{ label: 'Read the pilot results', to: '/case-studies' }}
      />

      <Footer />
    </Box>
  );
};

export default AboutUs;
